import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { getTournamentById, createTournament, ManageTournament, formatDate, getTournamentLogoUrl, updateTournamentHeroImage } from '../../api/tournament/manageTournamentService';
import secureApi from '../../utils/secureApi';
import { getImageUrl } from '../../utils/imageUrl';
import Breadcrumbs from '../../Common/Breadcrumbs';
import { CoverImageUpload } from '../../components/CoverImageUpload';
import { useTournamentSports } from '../../hooks/useTournamentSports';
// Import the SFL logo
import sflLogo from '../../assets/images/SFL Logo Blue.png';
import './TournamentDetailView.css';

const TournamentDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [tournament, setTournament] = useState<ManageTournament | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [editedTournament, setEditedTournament] = useState<ManageTournament | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [coverImageUploadSuccess, setCoverImageUploadSuccess] = useState(false);
  const [coverImageUploadError, setCoverImageUploadError] = useState<string | null>(null);
  const [heroImageUploadError, setHeroImageUploadError] = useState<string | null>(null);
  const [heroImageUploading, setHeroImageUploading] = useState(false);
  const [logoUploadSuccess, setLogoUploadSuccess] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const { options: sportsOptions } = useTournamentSports();

  useEffect(() => {
    // Check if edit mode or add mode was passed through navigation state
    const editModeFromState = location.state?.isEditMode;
    const addModeFromState = location.state?.isAddMode;
    
    if (addModeFromState) {
      // Add mode - initialize empty tournament
      setIsAddMode(true);
      setIsEditMode(true); // Enable edit mode for add functionality
      setLoading(false); // No need to load existing data
      
      // Initialize empty tournament data for add mode
      const emptyTournament: ManageTournament = {
        _id: '',
        name: '',
        shortName: '',
        logo: '',
        thumbnail: '',
        heroImage: '',
        location: '',
        startDate: '',
        endDate: '',
        registrationStartDate: '',
        registrationEndDate: '',
        status: 'upcoming',
        sports: '',
        ageCategories: [],
        isRegistrationOpen: true
      };
      
      setTournament(emptyTournament);
      setEditedTournament(emptyTournament);
      setHasChanges(true); // Enable save button for new tournament
    } else if (id) {
      // Load tournament details for view/edit mode
      loadTournamentDetails();
      
      // Set edit mode if passed through navigation state
      if (editModeFromState) {
        setIsEditMode(true);
      }
    }
  }, [id, location.state]);

  // Reset hasChanges when tournament data is loaded
  useEffect(() => {
    if (tournament && editedTournament) {
      console.log('Tournament data loaded, checking for changes...');
      const hasAnyChanges = checkForChanges(editedTournament, tournament);
      console.log('Initial change check result:', hasAnyChanges);
      setHasChanges(hasAnyChanges);
    }
  }, [tournament, editedTournament]);

  // Clear success messages after 3 seconds
  useEffect(() => {
    if (coverImageUploadSuccess) {
      const timer = setTimeout(() => {
        setCoverImageUploadSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [coverImageUploadSuccess]);

  useEffect(() => {
    if (logoUploadSuccess) {
      const timer = setTimeout(() => {
        setLogoUploadSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [logoUploadSuccess]);

  // Clear success state when component unmounts
  useEffect(() => {
    return () => {
      setSaveSuccess(false);
    };
  }, []);

  const loadTournamentDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Loading tournament details for ID:', id);
      
      const data = await getTournamentById(id!);
      console.log('🔍 Tournament data received:', data);
      console.log('🔍 Data type:', typeof data);
      console.log('🔍 Data keys:', Object.keys(data || {}));
      console.log('🔍 Short name value:', data?.shortName);
      
      if (data && data._id) {
        setTournament(data);
        setEditedTournament(data);
        // Don't reset hasChanges here - let the useEffect handle it
      } else {
        console.error('❌ Invalid tournament data structure:', data);
        setError('Invalid tournament data received from API');
      }
    } catch (err: any) {
      console.error('❌ Error loading tournament details:', err);
      setError(err.message || 'Failed to load tournament details');
    } finally {
      setLoading(false);
    }
  };

  // Function to check if there are any changes
  const checkForChanges = (currentData: ManageTournament, originalData: ManageTournament) => {
    if (!currentData || !originalData) return false;
    
    const fieldsToCheck: (keyof ManageTournament)[] = [
      'name', 'shortName', 'sports', 'logo', 'thumbnail', 'heroImage', 'location',
      'startDate', 'endDate', 'registrationStartDate', 'registrationEndDate'
    ];
    
    const hasChanges = fieldsToCheck.some(field => {
      const originalValue = originalData[field];
      const currentValue = currentData[field];
      
      // Handle date fields - normalize empty strings and null values
      if (field === 'startDate' || field === 'endDate' || 
          field === 'registrationStartDate' || field === 'registrationEndDate') {
        const normalizedOriginal = originalValue || '';
        const normalizedCurrent = currentValue || '';
        return normalizedOriginal !== normalizedCurrent;
      }
      
      // Handle string fields - normalize empty strings and null values
      if (typeof originalValue === 'string' && typeof currentValue === 'string') {
        const normalizedOriginal = (originalValue || '').trim();
        const normalizedCurrent = (currentValue || '').trim();
        return normalizedOriginal !== normalizedCurrent;
      }
      
      // Handle other fields - normalize null/undefined values
      const normalizedOriginal = originalValue || '';
      const normalizedCurrent = currentValue || '';
      return normalizedOriginal !== normalizedCurrent;
    });
    
    return hasChanges;
  };

  const hasOnlyHeroImageChanged = (currentData: ManageTournament, originalData: ManageTournament) => {
    const fieldsToCheck: (keyof ManageTournament)[] = [
      'name', 'shortName', 'sports', 'logo', 'thumbnail', 'location',
      'startDate', 'endDate', 'registrationStartDate', 'registrationEndDate'
    ];

    const nonHeroChanged = fieldsToCheck.some(field => {
      const originalValue = originalData[field] || '';
      const currentValue = currentData[field] || '';
      return String(originalValue).trim() !== String(currentValue).trim();
    });

    const originalHeroImage = originalData.heroImage || '';
    const currentHeroImage = currentData.heroImage || '';
    return !nonHeroChanged && originalHeroImage !== currentHeroImage;
  };

  const handleEdit = () => {
    setIsEditMode(true);
    // Reset editedTournament to original tournament data
    if (tournament) {
      setEditedTournament(tournament);
      setHasChanges(false);
    }
  };

  const handleSave = async () => {
    if (!editedTournament) return;
    
    // Validate required fields
    const requiredFields = {
      name: editedTournament.name?.trim(),
      shortName: editedTournament.shortName?.trim(),
      sports: editedTournament.sports?.trim(),
      location: editedTournament.location?.trim(),
      startDate: editedTournament.startDate?.trim(),
      endDate: editedTournament.endDate?.trim(),
      registrationStartDate: editedTournament.registrationStartDate?.trim(),
      registrationEndDate: editedTournament.registrationEndDate?.trim()
    };
    
    const missingFields = Object.entries(requiredFields)
      .filter(([field, value]) => !value)
      .map(([field]) => field);
    
    if (missingFields.length > 0) {
      const fieldNames = missingFields.map(field => {
        switch (field) {
          case 'name': return 'Tournament Name';
          case 'shortName': return 'Short Name';
          case 'sports': return 'Sports';
          case 'location': return 'Venue';
          case 'startDate': return 'Tournament Start Date';
          case 'endDate': return 'Tournament End Date';
          case 'registrationStartDate': return 'Registration Start Date';
          case 'registrationEndDate': return 'Registration End Date';
          default: return field;
        }
      }).join(', ');
      
      setError(`Please fill in all required fields: ${fieldNames}`);
      return;
    }
    
    try {
      // Prepare the data with all required fields
      const tournamentData = {
        name: editedTournament.name?.trim() || '',
        shortName: editedTournament.shortName?.trim() || '',
        sports: editedTournament.sports?.trim() || '',
        logo: editedTournament.logo || '',
        thumbnail: editedTournament.thumbnail || '',
        heroImage: editedTournament.heroImage || '',
        location: editedTournament.location?.trim() || '',
        startDate: editedTournament.startDate?.trim() || '',
        endDate: editedTournament.endDate?.trim() || '',
        registrationStartDate: editedTournament.registrationStartDate?.trim() || '',
        registrationEndDate: editedTournament.registrationEndDate?.trim() || ''
      };
      
      console.log(`${isAddMode ? 'Creating' : 'Saving'} tournament with data:`, tournamentData);
      
      let response;
      
      if (isAddMode) {
        // Create new tournament
        response = await createTournament(tournamentData);
        console.log('Tournament created successfully:', response);
      } else if (tournament && hasOnlyHeroImageChanged(editedTournament, tournament)) {
        if (!id) return;
        response = await updateTournamentHeroImage(id, editedTournament.heroImage || null);
        console.log('Tournament hero image updated successfully:', response);
      } else {
        // Update existing tournament
        if (!id) return;
        response = await secureApi.post(
          `/api/v2/tournaments/manage/update-tournament-details/${id}`,
          tournamentData
        );
        
        console.log('Tournament updated successfully:', response);
        
        // Handle different response structures for update
        if (response && response.data) {
          response = response.data;
        } else if (response && response._id) {
          response = response;
        } else {
          // If response structure is different, reload the data
          console.log('Response structure unclear, reloading tournament data...');
          await loadTournamentDetails();
          setIsEditMode(false);
          setHasChanges(false);
          // Set success state and redirect to listing page
          setSaveSuccess(true);
          setTimeout(() => {
            navigate('/manage-tournaments');
          }, 500); // Brief delay to show success state
          return;
        }
      }
      
      // Update the state with the correct data
      setTournament(response);
      setEditedTournament(response);
      
      setIsEditMode(false);
      setHasChanges(false);
      
      // Set success state and redirect to listing page
      setSaveSuccess(true);
      setTimeout(() => {
        navigate(isAddMode ? '/manage-tournaments-fixtures' : '/manage-tournaments');
      }, 500); // Brief delay to show success state
      
    } catch (error: any) {
      console.error(`Error ${isAddMode ? 'creating' : 'saving'} tournament:`, error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error URL:', error.config?.url);
      
      // Set error state for user feedback (could be displayed in UI)
      setError(error.response?.data?.message || `Failed to ${isAddMode ? 'create' : 'update'} tournament. Please try again.`);
      
      // Keep user in edit mode so they can try again
      setIsEditMode(true);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditedTournament(tournament);
    setHasChanges(false);
  };

  const handleDelete = () => {
    // TODO: Implement delete functionality
    console.log('Delete tournament:', tournament?._id);
  };

  const handleBack = () => {
    navigate(isAddMode ? '/manage-tournaments-fixtures' : '/manage-tournaments');
  };

  const handleInputChange = (field: keyof ManageTournament, value: any) => {
    // Clear any existing error when user starts editing
    if (error) {
      setError(null);
    }
    
    if (editedTournament) {
      const updatedTournament: any = {
        ...editedTournament,
        [field]: value
      };

      // Enforce date ordering
      if (field === 'startDate') {
        if (updatedTournament.endDate && new Date(updatedTournament.endDate) < new Date(value)) {
          updatedTournament.endDate = value;
        }
      }
      if (field === 'endDate') {
        if (updatedTournament.startDate && new Date(value) < new Date(updatedTournament.startDate)) {
          updatedTournament.endDate = updatedTournament.startDate;
        }
      }
      if (field === 'registrationStartDate') {
        if (updatedTournament.registrationEndDate && new Date(updatedTournament.registrationEndDate) < new Date(value)) {
          updatedTournament.registrationEndDate = value;
        }
      }
      if (field === 'registrationEndDate') {
        if (updatedTournament.registrationStartDate && new Date(value) < new Date(updatedTournament.registrationStartDate)) {
          updatedTournament.registrationEndDate = updatedTournament.registrationStartDate;
        }
      }
      
      setEditedTournament(updatedTournament);
      
      if (isAddMode) {
        // In add mode, enable save button if any field has a value
        const hasAnyValue = Object.values(updatedTournament).some(val => {
          if (Array.isArray(val)) return val.length > 0;
          return val && val.toString().trim() !== '';
        });
        setHasChanges(hasAnyValue);
      } else if (tournament) {
        // In edit mode, use the comprehensive change detection function
        const hasAnyChanges = checkForChanges(updatedTournament, tournament);
        setHasChanges(hasAnyChanges);
      }
    }
  };

  const handleTournamentLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset previous states
    setLogoUploadSuccess(false);
    setLogoUploadError(null);

    // Validate file
    if (!file.type.startsWith('image/')) {
      setLogoUploadError('Please select a valid image file.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setLogoUploadError('Image size should be less than 5MB.');
      return;
    }

    try {
      // Import imageCompression dynamically
      const imageCompression = (await import('browser-image-compression')).default;
      
      // Compress the image
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true
      });

      // Generate file name and context
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const ext = compressedFile.type.split('/')[1] || 'jpg';
      const fileName = `tournament_logo_${Date.now()}.${ext}`;
      const context = `tournaments-logo/${dd}-${mm}-${yyyy}`;

      // Get presigned URL using secureApi
      const presignRes = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: compressedFile.type,
          context,
        }
      );

      console.log('Tournament logo presigned URL response:', presignRes);
      console.log('Tournament logo response structure check:', {
        hasData: !!presignRes.data,
        dataKeys: presignRes.data ? Object.keys(presignRes.data) : 'no data',
        directKeys: Object.keys(presignRes || {}),
        presignedUrlFromData: presignRes.data?.presignedUrl,
        presignedUrlDirect: presignRes.presignedUrl,
        keyFromData: presignRes.data?.key,
        keyDirect: presignRes.key
      });

      // Handle both SecureApi and regular axios response formats (like AddClubDetails)
      const presignedUrl = presignRes.data?.presignedUrl || presignRes.presignedUrl;
      const s3Key = presignRes.data?.key || presignRes.key;
      
      if (!presignedUrl || !s3Key) {
        throw new Error('Failed to get presigned URL');
      }

      // Upload to S3
      console.log('Starting S3 upload with presigned URL:', presignedUrl);
      console.log('File type:', compressedFile.type, 'File size:', compressedFile.size);
      
      const uploadRes = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': compressedFile.type },
        body: compressedFile,
      });
      
      console.log('S3 upload response status:', uploadRes.status, uploadRes.statusText);
      
      if (!uploadRes.ok) {
        const errorText = await uploadRes.text();
        console.error('S3 upload failed:', uploadRes.status, uploadRes.statusText, errorText);
        throw new Error(`Failed to upload image to S3: ${uploadRes.status} ${uploadRes.statusText}`);
      }

      // Update form data with new logo
      if (editedTournament && tournament) {
        const updatedTournament = {
          ...editedTournament,
          logo: s3Key
        };
        setEditedTournament(updatedTournament);
        
        // Use the comprehensive change detection function
        const hasAnyChanges = checkForChanges(updatedTournament, tournament);
        console.log(`Tournament logo updated, hasChanges: ${hasAnyChanges}`);
        setHasChanges(hasAnyChanges);
        
        // Show success message
        setLogoUploadSuccess(true);
      }
      
    } catch (error) {
      console.error('Error uploading tournament logo:', error);
      setLogoUploadError('Failed to upload image. Please try again.');
    }
  };

  const handleCoverImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('handleCoverImageChange triggered');
    const file = event.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, 'Size:', file.size, 'Type:', file.type);

    // Reset previous states
    setCoverImageUploadSuccess(false);
    setCoverImageUploadError(null);

    // Validate file
    if (!file.type.startsWith('image/')) {
      setCoverImageUploadError('Please select a valid image file.');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setCoverImageUploadError('Image size should be less than 10MB.');
      return;
    }

    try {
      // Import imageCompression dynamically
      const imageCompression = (await import('browser-image-compression')).default;
      
      // Compress the image (same as banner management)
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      });

      // Generate file name and context (correct path structure)
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const ext = compressedFile.type.split('/')[1] || 'jpg';
      const fileName = `tournament_cover_${Date.now()}.${ext}`;
      const context = `tournaments-cover-image/${dd}-${mm}-${yyyy}`;

      console.log('Generated file info:', { fileName, context, ext });

      // Get presigned URL using secureApi
      console.log('Getting presigned URL...');
      const presignRes = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: compressedFile.type,
          context,
        }
      );

      console.log('Presigned URL response:', presignRes);
      console.log('Response structure check:', {
        hasData: !!presignRes.data,
        dataKeys: presignRes.data ? Object.keys(presignRes.data) : 'no data',
        directKeys: Object.keys(presignRes || {}),
        presignedUrlFromData: presignRes.data?.presignedUrl,
        presignedUrlDirect: presignRes.presignedUrl,
        keyFromData: presignRes.data?.key,
        keyDirect: presignRes.key
      });

      // Handle both SecureApi and regular axios response formats (like AddClubDetails)
      const presignedUrl = presignRes.data?.presignedUrl || presignRes.presignedUrl;
      const s3Key = presignRes.data?.key || presignRes.key;
      
      if (!presignedUrl || !s3Key) {
        console.error('Missing presigned URL or S3 key:', { presignedUrl, s3Key });
        throw new Error('Failed to get presigned URL');
      }

      console.log('Uploading to S3 with key:', s3Key);

      // Upload to S3
      console.log('Starting S3 upload with presigned URL:', presignedUrl);
      console.log('File type:', compressedFile.type, 'File size:', compressedFile.size);
      
      const uploadRes = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': compressedFile.type },
        body: compressedFile,
      });
      
      console.log('S3 upload response status:', uploadRes.status, uploadRes.statusText);
      
      if (!uploadRes.ok) {
        const errorText = await uploadRes.text();
        console.error('S3 upload failed:', uploadRes.status, uploadRes.statusText, errorText);
        throw new Error(`Failed to upload image to S3: ${uploadRes.status} ${uploadRes.statusText}`);
      }

      console.log('S3 upload successful, updating tournament with thumbnail key:', s3Key);

      // Update form data with new thumbnail image
      if (editedTournament && tournament) {
        const updatedTournament = {
          ...editedTournament,
          thumbnail: s3Key
        };
        setEditedTournament(updatedTournament);
        
        // Use the comprehensive change detection function
        const hasAnyChanges = checkForChanges(updatedTournament, tournament);
        console.log(`Cover image updated, hasChanges: ${hasAnyChanges}`);
        setHasChanges(hasAnyChanges);
        
        // Show success message
        setCoverImageUploadSuccess(true);
      }
      
    } catch (error) {
      console.error('Error uploading cover image:', error);
      setCoverImageUploadError('Failed to upload image. Please try again.');
    }
  };

  const handleCoverImageRemove = () => {
    console.log('Removing cover image...');
    if (editedTournament && tournament) {
      const updatedTournament = {
        ...editedTournament,
        thumbnail: ''
      };
      setEditedTournament(updatedTournament);
      
      // Use the comprehensive change detection function
      const hasAnyChanges = checkForChanges(updatedTournament, tournament);
      console.log(`Cover image removed, hasChanges: ${hasAnyChanges}`);
      setHasChanges(hasAnyChanges);
      
      // Show success message
      setCoverImageUploadSuccess(true);
      setCoverImageUploadError(null);
    }
  };

  const handleHeroImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/') || file.size > 10 * 1024 * 1024) {
      setHeroImageUploadError('Select an image under 10 MB.');
      return;
    }

    setHeroImageUploadError(null);
    setHeroImageUploading(true);
    try {
      const imageCompression = (await import('browser-image-compression')).default;
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      });
      const date = new Date();
      const folder = `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
      const presign = await secureApi.post('/api/v1/general/presigned-url', {
        fileName: `tournament_card_${Date.now()}.${compressedFile.type.split('/')[1] || 'jpg'}`,
        contentType: compressedFile.type,
        context: `tournaments-card-image/${folder}`,
      });
      const presignedUrl = presign.data?.presignedUrl || presign.presignedUrl;
      const key = presign.data?.key || presign.key;
      if (!presignedUrl || !key) throw new Error('Could not start the image upload.');
      const uploaded = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': compressedFile.type },
        body: compressedFile,
      });
      if (!uploaded.ok) throw new Error('Image upload failed.');
      if (editedTournament && tournament) {
        const updatedTournament = { ...editedTournament, heroImage: key };
        setEditedTournament(updatedTournament);
        setHasChanges(checkForChanges(updatedTournament, tournament));
      }
    } catch (uploadError) {
      setHeroImageUploadError(uploadError instanceof Error ? uploadError.message : 'Image upload failed.');
    } finally {
      setHeroImageUploading(false);
    }
  };

  const handleHeroImageRemove = () => {
    if (!editedTournament || !tournament) return;
    const updatedTournament = { ...editedTournament, heroImage: '' };
    setEditedTournament(updatedTournament);
    setHasChanges(checkForChanges(updatedTournament, tournament));
    setHeroImageUploadError(null);
  };

  if (loading) {
    return (
      <div className="tournament-detail-page">
        <Container fluid>
          <div className="loading-container">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error && !isAddMode) {
    return (
      <div className="tournament-detail-page">
        <Container fluid>
          <div className="error-container">
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
            <Button variant="outline-primary" onClick={handleBack}>
              Back to Manage Tournaments
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  // For non-add mode, require tournament data
  if (!isAddMode && !tournament) {
    return (
      <div className="tournament-detail-page">
        <Container fluid>
          <div className="error-container">
            <div className="alert alert-danger" role="alert">
              Tournament not found
            </div>
            <Button variant="outline-primary" onClick={handleBack}>
              Back to Manage Tournaments
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  const currentData = isEditMode ? editedTournament : tournament;

  // Ensure we have currentData before rendering the form
  if (!currentData) {
    return (
      <div className="tournament-detail-page">
        <Container fluid>
          <div className="error-container">
            <div className="alert alert-danger" role="alert">
              No tournament data available
            </div>
            <Button variant="outline-primary" onClick={handleBack}>
              Back to Manage Tournaments
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  // Debug logging for current state
  console.log('🔍 Current component state:', {
    loading,
    isEditMode,
    isAddMode,
    tournament: tournament?._id,
    editedTournament: editedTournament?._id,
    currentData: currentData?._id,
    shortName: currentData?.shortName,
    hasChanges
  });

  const breadcrumbItems = [
    { label: 'Manage Tournaments', onClick: () => navigate('/manage-tournaments') },
    { label: isAddMode ? 'Add New Tournament' : (isEditMode ? 'Edit Tournament' : 'Tournament Details'), active: true }
  ];

  return (
    <div className="tournament-detail-page">
      <Container fluid>
        {/* Breadcrumbs - Matching Manage Tournaments Style */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        {/* Success Message */}
        {saveSuccess && (
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <div className="alert alert-success d-flex align-items-center" role="alert">
              <i className="ri-check-line me-2"></i>
              Tournament {isAddMode ? 'created' : 'updated'} successfully! Redirecting to listing page...
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </div>
          </div>
        )}

        {/* Page Header with Back Button and Tournament Logo */}
        <div className="page-header-section mb-4" style={{ padding: '0 15px' }}>
          <div className="d-flex align-items-center" style={{ gap: '16px', marginBottom: '24px' }}>
            <button 
              className="back-button"
                onClick={handleBack}
              >
                <i className="ri-arrow-left-line"></i>
            </button>
            {currentData && (
              <>
                <div className="tournament-logo-small" style={{ flexShrink: 0 }}>
                  <img 
                    src={getTournamentLogoUrl(currentData) || sflLogo} 
                    alt={currentData.name || 'Tournament Logo'}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = sflLogo;
                    }}
                  />
                </div>
                <div className="d-flex align-items-center" style={{ gap: '8px', flex: 1 }}>
                  <h1 className="page-title mb-0">
                    {isAddMode ? 'Add New Tournament' : currentData.name}
                    {isEditMode && !isAddMode && (
                      <span className="ms-2" style={{ fontSize: '16px', color: '#6c757d', fontWeight: 'normal' }}>
                        (Editing)
                      </span>
                    )}
                    {isAddMode && (
                      <span className="ms-2" style={{ fontSize: '16px', color: '#6c757d', fontWeight: 'normal' }}>
                        (Creating)
                      </span>
                    )}
              </h1>
            </div>
              </>
            )}
            
            {/* Action Buttons */}
            <div className="d-flex gap-2">
              {!isEditMode ? (
                <>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleEdit}
                    style={{
                      background: '#006EFF',
                      border: 'none',
                      color: '#fff',
                      fontFamily: 'Inter, Arial, sans-serif',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      minWidth: '80px'
                    }}
                  >
                    <i className="ri-edit-line me-1"></i>
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleDelete}
                    style={{
                      background: '#dc3545',
                      border: 'none',
                      color: '#fff',
                      fontFamily: 'Inter, Arial, sans-serif',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      minWidth: '80px'
                    }}
                  >
                    <i className="ri-delete-bin-line me-1"></i>
                    Delete
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSave}
                    className="me-2"
                    disabled={!hasChanges || heroImageUploading}
                    title={`${isAddMode ? 'Create' : 'Save'} button - hasChanges: ${hasChanges}`}
                  >
                    <i className="ri-save-line me-1"></i>
                    {isAddMode ? 'Create Tournament' : 'Save'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleCancel}
                  >
                    <i className="ri-close-line me-1"></i>
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Main Content - Three Separate Rows */}
        
        {/* Row 1: Cover Image - Club Style Layout */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="tournament-form-card">
              <Card.Header>
                <h5 className="card-title">
                  <i className="ri-image-line me-2"></i>
                  Cover Image
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="cover-image-section">
                  <div className="cover-image-upload-area">
                    {currentData.thumbnail ? (
                      <div 
                        className="cover-image-preview"
                        style={{
                          backgroundImage: `url(${getTournamentLogoUrl({ ...currentData, logo: currentData.thumbnail })})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat'
                        }}
                        onClick={() => {
                          if (isEditMode) {
                            console.log('Cover image preview clicked, triggering file input...');
                            const fileInput = document.getElementById('cover-image-input') as HTMLInputElement;
                            if (fileInput) {
                              fileInput.click();
                            } else {
                              console.error('Cover image file input not found');
                            }
                          }
                        }}
                      >
                        {isEditMode && (
                          <div className="cover-image-overlay">
                            <i className="ri-camera-line"></i>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div 
                        className="cover-image-upload-placeholder"
                        onClick={() => {
                          if (isEditMode) {
                            console.log('Cover image placeholder clicked, triggering file input...');
                            const fileInput = document.getElementById('cover-image-input') as HTMLInputElement;
                            if (fileInput) {
                              fileInput.click();
                            } else {
                              console.error('Cover image file input not found');
                            }
                          }
                        }}
                      >
                        <i className="ri-image-add-line upload-icon"></i>
                        <div className="upload-text">Upload Cover Image</div>
                      </div>
                    )}
                    
                    <input
                      id="cover-image-input"
                      type="file"
                      accept="image/*"
                      onChange={handleCoverImageChange}
                      style={{ display: 'none' }}
                    />
                  </div>
                  
                  {isEditMode && (
                    <div className="cover-image-info">
                      <small className="text-muted">
                        <i className="ri-information-line me-1"></i>
                        Supported formats: JPG, PNG, GIF. Max size: 1MB.
                      </small>
                      {currentData.thumbnail && (
                        <div className="mt-2">
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={handleCoverImageRemove}
                          >
                            <i className="ri-delete-bin-line me-1"></i>
                            Remove Cover Image
                          </Button>
                        </div>
                      )}
                      
                      {/* Cover image upload error and success */}
                      {coverImageUploadError && (
                        <div className="document-upload-error mt-2">
                          <i className="ri-error-warning-line"></i>
                          {coverImageUploadError}
                        </div>
                      )}
                      
                      {coverImageUploadSuccess && (
                        <div className="document-upload-success mt-2">
                          <i className="ri-check-line"></i>
                          Cover image uploaded successfully!
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={12}>
            <Card className="tournament-form-card">
              <Card.Header>
                <h5 className="card-title mb-0">Tournament hero image</h5>
              </Card.Header>
              <Card.Body>
                <p className="text-muted small">Stored as tournament hero artwork. Season hero images manage the current SFL TV league-card artwork.</p>
                {currentData.heroImage ? (
                  <img
                    src={getImageUrl(currentData.heroImage)}
                    alt="Tournament hero preview"
                    className="rounded border d-block mb-3"
                    style={{ width: '100%', maxWidth: 360, aspectRatio: '16 / 9', objectFit: 'cover' }}
                  />
                ) : (
                  <div className="text-muted mb-3">No tournament card image configured; the tournament cover image is used.</div>
                )}
                {isEditMode && (
                  <>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      aria-label="Upload tournament hero image"
                      onChange={handleHeroImageChange}
                      disabled={heroImageUploading}
                      className="mb-2"
                    />
                    {currentData.heroImage && (
                      <Button size="sm" variant="outline-danger" onClick={handleHeroImageRemove} disabled={heroImageUploading}>
                        Remove hero image
                      </Button>
                    )}
                    <div className="small text-muted mt-2">Save the tournament to publish this change.</div>
                  </>
                )}
                {heroImageUploadError && <div className="alert alert-danger mt-2 mb-0" role="alert">{heroImageUploadError}</div>}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Row 2: Tournament Details - Full Width */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="tournament-form-card">
              <Card.Header>
                <h5 className="card-title">
                  <i className="ri-trophy-line me-2"></i>
                  Tournament Details
                </h5>
              </Card.Header>
              <Card.Body>
                {/* First Row: Tournament Logo */}
                <Row className="mb-4">
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Tournament Logo</label>
                      <div className="tournament-logo-upload-section">
                        <div className="tournament-logo-upload">
                          <div 
                            className="tournament-logo-preview"
                            style={{
                              background: currentData.logo && currentData.logo !== 'logo' ? `url(${getTournamentLogoUrl(currentData) || ''}) center/cover` : '#f8f9fa'
                            }}
                            onClick={() => isEditMode && document.getElementById('tournament-logo-input')?.click()}
                          >
                            {!currentData.logo || currentData.logo === 'logo' ? (
                              <div className="upload-placeholder">
                                <i className="ri-image-add-line upload-icon"></i>
                                <div className="upload-text">Upload Logo</div>
                        </div>
                            ) : null}
                          </div>
                          <input
                            id="tournament-logo-input"
                            type="file"
                            accept="image/*"
                            onChange={handleTournamentLogoChange}
                            style={{ display: 'none' }}
                          />
                        </div>
                        
                        {/* Tournament logo upload error and success */}
                        {logoUploadError && (
                          <div className="document-upload-error mt-2">
                            <i className="ri-error-warning-line"></i>
                            {logoUploadError}
                          </div>
                        )}
                        
                        {logoUploadSuccess && (
                          <div className="document-upload-success mt-2">
                            <i className="ri-check-line"></i>
                            Tournament logo uploaded successfully!
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Tournament Details Fields - Clean Layout */}
                <div className="tournament-fields-container">
                  {/* First Row: Tournament Name and Short Name */}
                  <div className="field-row">
                    <div className="field-group">
                      <label>Tournament Name*</label>
                      {isEditMode ? (
                        <Form.Control
                          type="text"
                          value={currentData.name || ''}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="Enter tournament name"
                          className="form-field"
                        />
                      ) : (
                        <div className="field-value">{currentData.name || 'N/A'}</div>
                      )}
                    </div>

                    <div className="field-group">
                      <label>Short Name*</label>
                      {isEditMode ? (
                        <Form.Control
                          type="text"
                          value={currentData.shortName || ''}
                          onChange={(e) => handleInputChange('shortName', e.target.value)}
                          placeholder="Enter short name"
                          className="form-field"
                        />
                      ) : (
                        <div className="field-value">{currentData.shortName || 'N/A'}</div>
                      )}
                    </div>
                  </div>

                  {/* Second Row: Sports and Venue */}
                  <div className="field-row">
                    <div className="field-group">
                      <label>Sports*</label>
                      {isEditMode ? (
                        <Select
                          classNamePrefix="react-select"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                          value={sportsOptions.find(option => option.value === currentData.sports)}
                          onChange={(selectedOption) => handleInputChange('sports', selectedOption?.value || '')}
                          options={sportsOptions}
                          placeholder="Select Sports"
                          className="form-field"
                        />
                      ) : (
                        <div className="field-value">
                          <Badge bg="success">{currentData.sports || 'N/A'}</Badge>
                        </div>
                      )}
                    </div>

                    <div className="field-group">
                      <label>Venue*</label>
                      {isEditMode ? (
                        <Form.Control
                          type="text"
                          value={currentData.location || ''}
                          onChange={(e) => handleInputChange('location', e.target.value)}
                          placeholder="Enter venue address"
                          className="form-field"
                        />
                      ) : (
                        <div className="field-value">{currentData.location || 'N/A'}</div>
                      )}
                    </div>
                  </div>

                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Row 3: Date and Timeline - Full Width */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="tournament-form-card">
              <Card.Header>
                <h5 className="card-title">
                  <i className="ri-calendar-line me-2"></i>
                  Date and Timeline
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="timeline-fields-container">
                  {/* Registration Timeline */}
                  <div className="timeline-section">
                    <h6 className="timeline-title">Registration Timeline</h6>
                    
                    <div className="field-group">
                      <label>Registration Start Date*</label>
                      {isEditMode ? (
                        <Form.Control
                          type="date"
                          value={currentData.registrationStartDate ? currentData.registrationStartDate.split('T')[0] : ''}
                          onChange={(e) => handleInputChange('registrationStartDate', e.target.value)}
                          className="form-field"
                        />
                      ) : (
                        <div className="field-value">
                          {currentData.registrationStartDate ? formatDate(currentData.registrationStartDate) : 'Not set'}
                        </div>
                      )}
                    </div>

                    <div className="field-group">
                      <label>Registration End Date*</label>
                      {isEditMode ? (
                        <Form.Control
                          type="date"
                          value={currentData.registrationEndDate ? currentData.registrationEndDate.split('T')[0] : ''}
                          onChange={(e) => handleInputChange('registrationEndDate', e.target.value)}
                          className="form-field"
                          min={currentData.registrationStartDate ? currentData.registrationStartDate.split('T')[0] : ''}
                        />
                      ) : (
                        <div className="field-value">
                          {currentData.registrationEndDate ? formatDate(currentData.registrationEndDate) : 'Not set'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tournament Timeline */}
                  <div className="timeline-section">
                    <h6 className="timeline-title">Tournament Timeline</h6>
                    
                    <div className="field-group">
                      <label>Tournament Start Date*</label>
                      {isEditMode ? (
                        <Form.Control
                          type="date"
                          value={currentData.startDate ? currentData.startDate.split('T')[0] : ''}
                          onChange={(e) => handleInputChange('startDate', e.target.value)}
                          className="form-field"
                        />
                      ) : (
                        <div className="field-value">{formatDate(currentData.startDate)}</div>
                      )}
                    </div>

                    <div className="field-group">
                      <label>Tournament End Date*</label>
                      {isEditMode ? (
                        <Form.Control
                          type="date"
                          value={currentData.endDate ? currentData.endDate.split('T')[0] : ''}
                          onChange={(e) => handleInputChange('endDate', e.target.value)}
                          className="form-field"
                          min={currentData.startDate ? currentData.startDate.split('T')[0] : ''}
                        />
                      ) : (
                        <div className="field-value">{formatDate(currentData.endDate)}</div>
                      )}
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Action Button */}
        {isEditMode && (
          <div className="action-button-section">
            <Button 
              variant="primary" 
              size="lg" 
              onClick={handleSave}
              disabled={!hasChanges || heroImageUploading}
              title={`Bottom ${isAddMode ? 'Create' : 'Save'} button - hasChanges: ${hasChanges}`}
            >
              <i className="ri-save-line me-2"></i>
              {isAddMode ? 'Create Tournament' : 'Save Changes'}
            </Button>
          </div>
        )}
      </Container>
    </div>
  );
};

export default TournamentDetailView;
